import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import './App.css';
import authService, { AuthUser } from './services/authService';
import { MusicEditorProvider } from './contexts/MusicEditorContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import SkipLinks from './components/SkipLinks';
// import { performanceMonitor } from './utils/performanceMonitoring';

// Lazy loaded pages for code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const JamGroupsPage = lazy(() => import('./pages/JamGroupsPage'));
const MarketingLandingPage = lazy(() => import('./pages/MarketingLandingPage'));
const MusicEditorPage = lazy(() => import('./pages/MusicEditorPage'));
const PlaylistsPage = lazy(() => import('./pages/PlaylistsPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));

// Loading component for lazy loaded routes
const PageLoadingSpinner = () => (
  <div
    className="page-loading-container"
    role="status"
    aria-live="polite"
    aria-label="Loading page"
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: '16px',
    }}
  >
    <div
      className="spinner"
      aria-hidden="true"
      style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
    <p>Loading page...</p>
  </div>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);

  // Track performance budgets (temporarily disabled)
  // useEffect(() => {
  //   const checkBudgets = () => {
  //     const budgetStatus = performanceMonitor.checkPerformanceBudgets();
  //     if (
  //       budgetStatus.bundleBudgetExceeded ||
  //       budgetStatus.memoryBudgetExceeded
  //     ) {
  //       console.warn('Performance budgets exceeded:', budgetStatus.issues);
  //     }
  //   };

  //   // Check budgets on initial load and then periodically
  //   checkBudgets();
  //   const intervalId = setInterval(checkBudgets, 60000); // Every minute

  //   return () => clearInterval(intervalId);
  // }, []);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const authenticated = await authService.isAuthenticated();
      if (authenticated) {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthenticated = (userData: AuthUser) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleUserUpdated = (userData: AuthUser) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div
        className="app-loading"
        role="status"
        aria-live="polite"
        aria-label="Loading application"
      >
        <div className="spinner" aria-hidden="true"></div>
        <p>Loading JamSession...</p>
      </div>
    );
  }

  return (
    <AccessibilityProvider>
      <MusicEditorProvider>
        <Router>
          <div className="App">
            <SkipLinks />

            {/* Screen reader announcement region */}
            <div
              id="accessibility-announcements"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            />

            <Suspense fallback={<PageLoadingSpinner />}>
              <Routes>
                {isAuthenticated ? (
                  // Authenticated routes
                  <>
                    <Route
                      path="/"
                      element={
                        <DashboardPage user={user} onLogout={handleLogout} />
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <DashboardPage user={user} onLogout={handleLogout} />
                      }
                    />
                    <Route path="/editor" element={<MusicEditorPage />} />
                    <Route
                      path="/editor/:songId"
                      element={<MusicEditorPage />}
                    />
                    <Route
                      path="/profile"
                      element={
                        <UserProfilePage
                          user={user}
                          onLogout={handleLogout}
                          onUserUpdated={handleUserUpdated}
                        />
                      }
                    />
                    <Route
                      path="/groups"
                      element={
                        <JamGroupsPage user={user} onLogout={handleLogout} />
                      }
                    />
                    <Route
                      path="/playlists"
                      element={
                        <PlaylistsPage user={user} onLogout={handleLogout} />
                      }
                    />
                    <Route
                      path="/auth"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route
                      path="/login"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route
                      path="/register"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route
                      path="/registration"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="/dashboard" replace />}
                    />
                  </>
                ) : (
                  // Unauthenticated routes
                  <>
                    <Route path="/" element={<MarketingLandingPage />} />
                    <Route
                      path="/dashboard"
                      element={<MarketingLandingPage />}
                    />
                    <Route
                      path="/auth"
                      element={
                        <MarketingLandingPage
                          authMode="login"
                          onAuthenticated={handleAuthenticated}
                        />
                      }
                    />
                    <Route
                      path="/login"
                      element={
                        <MarketingLandingPage
                          authMode="login"
                          onAuthenticated={handleAuthenticated}
                        />
                      }
                    />
                    <Route
                      path="/register"
                      element={
                        <MarketingLandingPage
                          authMode="register"
                          onAuthenticated={handleAuthenticated}
                        />
                      }
                    />
                    <Route
                      path="/registration"
                      element={<Navigate to="/register" replace />}
                    />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                )}
              </Routes>
            </Suspense>
          </div>
        </Router>
      </MusicEditorProvider>
    </AccessibilityProvider>
  );
}

export default App;
